import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import { ArrowLeft, Coins, Zap, TrendingDown, CheckCircle, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  image_generation: "Image Generation",
  video_generation: "Video Generation",
  script_generation: "Script Writing",
  character_generation: "Character Design",
  storyboard_generation: "Storyboard",
  speech_generation: "Text to Speech",
  concept_generation: "Concept Gen",
  adconcepts_generation: "Ad Concepts",
  adimages_generation: "Ad Images",
  video_production_prompt_generation: "Production Prompts",
  video_prompt_generation: "Video Prompts",
  frame_prompts_generation: "Frame Prompts",
  audio_transcription: "Transcription",
  image_editing: "Image Editing",
  lipsync_generation: "Lip Sync",
  voice_cloning: "Voice Cloning",
};

const COLORS = [
  "#6366f1", "#f43f5e", "#10b981", "#f59e0b", "#3b82f6",
  "#8b5cf6", "#ec4899", "#14b8a6", "#ef4444", "#06b6d4",
  "#a855f7", "#84cc16", "#e879f9", "#22d3ee", "#fb923c", "#64748b"
];

const formatLabel = (key) => FEATURE_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-xl border border-slate-700">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{p.value.toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-xl border border-slate-700">
      <p className="font-medium">{d.name}</p>
      <p style={{ color: d.payload.fill }}>{d.value.toLocaleString()} credits ({d.payload.percent}%)</p>
    </div>
  );
};

export default function CreditAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/credits/analytics`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => console.error("Analytics load failed:", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4">
        <p>Failed to load analytics</p>
        <Button variant="outline" onClick={() => navigate("/credits")}>Back to Credits</Button>
      </div>
    );
  }

  const { feature_breakdown, daily_spend, summary } = data;
  const totalFeatureCredits = feature_breakdown.reduce((s, f) => s + f.total_credits, 0);

  const pieData = feature_breakdown.slice(0, 8).map((f, i) => ({
    name: formatLabel(f.feature),
    value: f.total_credits,
    fill: COLORS[i % COLORS.length],
    percent: totalFeatureCredits ? Math.round((f.total_credits / totalFeatureCredits) * 100) : 0
  }));

  const barData = feature_breakdown.map((f, i) => ({
    name: formatLabel(f.feature),
    credits: f.total_credits,
    jobs: f.count,
    fill: COLORS[i % COLORS.length]
  }));

  const areaData = daily_spend.map(d => ({
    date: d.date.slice(5),
    spent: d.spent,
    jobs: d.count
  }));

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate("/credits")}
              className="text-slate-400 hover:text-white mb-2 -ml-3"
              data-testid="back-to-credits-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Credits
            </Button>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Usage <span className="text-indigo-400">Analytics</span>
            </h1>
            <p className="text-slate-400 mt-1 text-sm">Per-feature credit spend over time</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="analytics-summary">
          <SummaryCard icon={<Coins className="w-4 h-4" />} label="Available" value={summary.available_credits} color="text-emerald-400" />
          <SummaryCard icon={<TrendingDown className="w-4 h-4" />} label="Total Used" value={summary.total_used} color="text-rose-400" />
          <SummaryCard icon={<Zap className="w-4 h-4" />} label="Purchased" value={summary.total_purchased} color="text-amber-400" />
          <SummaryCard icon={<CheckCircle className="w-4 h-4" />} label="Completed" value={summary.total_completed_jobs} color="text-indigo-400" />
          <SummaryCard icon={<XCircle className="w-4 h-4" />} label="Failed" value={summary.total_failed_jobs} color="text-slate-400" />
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-5 gap-4">
          {/* Daily Spend Area Chart */}
          <Card className="lg:col-span-3 bg-slate-900 border-slate-800" data-testid="daily-spend-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-slate-200">Daily Spend (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              {areaData.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-12">No spend data in the last 30 days</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={areaData}>
                    <defs>
                      <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="spent" name="Credits" stroke="#6366f1" fill="url(#spendGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card className="lg:col-span-2 bg-slate-900 border-slate-800" data-testid="feature-pie-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-slate-200">Spend by Feature</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              {pieData.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-12">No usage data yet</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%" cy="50%"
                        innerRadius={55} outerRadius={85}
                        dataKey="value"
                        paddingAngle={2}
                        stroke="none"
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 justify-center">
                    {pieData.map((d, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs text-slate-300">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ background: d.fill }} />
                        {d.name} ({d.percent}%)
                      </span>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Per-Feature Bar Chart */}
        <Card className="bg-slate-900 border-slate-800" data-testid="feature-bar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-slate-200">Credits by Feature (All Time)</CardTitle>
          </CardHeader>
          <CardContent>
            {barData.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-12">No usage data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(260, barData.length * 36)}>
                <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fill: "#cbd5e1", fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="credits" name="Credits" radius={[0, 4, 4, 0]} barSize={20}>
                    {barData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Feature Table */}
        <Card className="bg-slate-900 border-slate-800" data-testid="feature-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-slate-200">Feature Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-800">
                    <th className="text-left py-2 pr-4">Feature</th>
                    <th className="text-right py-2 px-4">Jobs</th>
                    <th className="text-right py-2 px-4">Credits</th>
                    <th className="text-right py-2 pl-4">Avg/Job</th>
                  </tr>
                </thead>
                <tbody>
                  {feature_breakdown.map((f, i) => (
                    <tr key={f.feature} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="py-2.5 pr-4 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-slate-200">{formatLabel(f.feature)}</span>
                      </td>
                      <td className="text-right py-2.5 px-4 text-slate-300">{f.count.toLocaleString()}</td>
                      <td className="text-right py-2.5 px-4 font-medium text-white">{f.total_credits.toLocaleString()}</td>
                      <td className="text-right py-2.5 pl-4 text-slate-400">{f.count ? Math.round(f.total_credits / f.count) : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-4">
        <div className={`flex items-center gap-1.5 text-xs ${color} mb-1`}>
          {icon} {label}
        </div>
        <p className="text-xl font-bold text-white">{(value || 0).toLocaleString()}</p>
      </CardContent>
    </Card>
  );
}
